import { useEffect, useState } from 'react';
import { createApi } from '../services/api.js';

interface Analysis {
  id: string;
  toxicity_score: number;
  primary_category: string;
  primary_severity: string;
  action_taken: string;
  analyzed_at: string;
  analysis_mode?: 'shield' | 'sender';
}

function scoreStyle(score: number) {
  if (score <= 20) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', barColor: 'bg-emerald-500', label: 'Clean' };
  if (score <= 40) return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', barColor: 'bg-yellow-500', label: 'Mild' };
  if (score <= 60) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', barColor: 'bg-orange-500', label: 'Warning' };
  if (score <= 75) return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', barColor: 'bg-red-500', label: 'Toxic' };
  return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', barColor: 'bg-red-600', label: 'Critical' };
}

function formatCategory(cat: string) {
  if (!cat || cat === 'none') return 'Clean';
  return cat.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

function sevColor(sev: string) {
  switch (sev) {
    case 'critical': return 'border-red-200 bg-red-50 text-red-700';
    case 'high': return 'border-orange-200 bg-orange-50 text-orange-700';
    case 'medium': return 'border-yellow-200 bg-yellow-50 text-yellow-700';
    default: return 'border-surface-200 bg-surface-100 text-ink-muted';
  }
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getScoreDistribution(analyses: Analysis[]) {
  const buckets = [
    { label: 'Clean', range: '0–20', count: 0, color: 'bg-emerald-500' },
    { label: 'Mild', range: '21–40', count: 0, color: 'bg-yellow-500' },
    { label: 'Warning', range: '41–60', count: 0, color: 'bg-orange-500' },
    { label: 'Toxic', range: '61–75', count: 0, color: 'bg-red-500' },
    { label: 'Critical', range: '76–100', count: 0, color: 'bg-red-700' },
  ];
  for (const a of analyses) {
    if (a.toxicity_score <= 20) buckets[0].count++;
    else if (a.toxicity_score <= 40) buckets[1].count++;
    else if (a.toxicity_score <= 60) buckets[2].count++;
    else if (a.toxicity_score <= 75) buckets[3].count++;
    else buckets[4].count++;
  }
  return buckets;
}

function getTopCategories(analyses: Analysis[]) {
  const counts: Record<string, number> = {};
  for (const a of analyses) {
    if (a.primary_category && a.primary_category !== 'none') {
      counts[a.primary_category] = (counts[a.primary_category] ?? 0) + 1;
    }
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
}

function getTrend(analyses: Analysis[]): { direction: 'up' | 'down' | 'stable'; recent: number; previous: number } {
  if (analyses.length < 4) return { direction: 'stable', recent: 0, previous: 0 };
  const half = Math.floor(analyses.length / 2);
  const recent = analyses.slice(0, half);
  const previous = analyses.slice(half);
  const recentAvg = recent.reduce((s, a) => s + a.toxicity_score, 0) / recent.length;
  const prevAvg = previous.reduce((s, a) => s + a.toxicity_score, 0) / previous.length;
  const diff = recentAvg - prevAvg;
  return {
    direction: diff > 3 ? 'up' : diff < -3 ? 'down' : 'stable',
    recent: Math.round(recentAvg),
    previous: Math.round(prevAvg),
  };
}

export function DashboardPage({ token }: { token: string }) {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const api = createApi(token);
    api.getAnalyses(1, 100).then((result) => {
      if (result.ok) {
        setAnalyses(result.data.analyses ?? result.data ?? []);
      }
      setLoading(false);
    });
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-300 border-t-brand" />
      </div>
    );
  }

  const total = analyses.length;
  const avgScore = total > 0 ? Math.round(analyses.reduce((s, a) => s + a.toxicity_score, 0) / total) : 0;
  const flagged = analyses.filter(a => a.toxicity_score > 60).length;
  const quarantined = analyses.filter(a => a.action_taken === 'quarantined').length;
  const distribution = getScoreDistribution(analyses);
  const maxBucket = Math.max(...distribution.map(b => b.count), 1);
  const topCategories = getTopCategories(analyses);
  const trend = getTrend(analyses);
  const avgStyle = scoreStyle(avgScore);

  const trendIcon = trend.direction === 'down' ? '\u2193' : trend.direction === 'up' ? '\u2191' : '\u2192';
  const trendColor = trend.direction === 'down' ? 'text-emerald-600' : trend.direction === 'up' ? 'text-red-600' : 'text-ink-muted';
  const trendLabel = trend.direction === 'down' ? 'Improving' : trend.direction === 'up' ? 'Worsening' : 'Stable';

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl font-bold text-ink">Dashboard</h1>
        <p className="text-sm text-ink-muted mt-1">
          Your workplace communication health at a glance
        </p>
      </div>

      {total === 0 ? (
        <div className="rounded-xl border border-dashed border-surface-200 bg-white p-12 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-100">
            <svg className="h-6 w-6 text-surface-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-ink">No analyses yet</p>
          <p className="text-xs text-ink-light mt-1">Install the Chrome Extension or Gmail Add-on to start analyzing emails.</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-surface-200 bg-white p-5">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Analyzed</p>
              <p className="mt-2 text-3xl font-black text-ink tabular-nums">{total}</p>
              <p className="text-[11px] text-ink-light mt-1">emails scanned</p>
            </div>

            <div className={`rounded-xl border ${avgStyle.border} bg-white p-5`}>
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Avg Score</p>
              <p className={`mt-2 text-3xl font-black tabular-nums ${avgStyle.text}`}>{avgScore}</p>
              <p className={`text-[11px] mt-1 ${avgStyle.text}`}>{avgStyle.label}</p>
            </div>

            <div className="rounded-xl border border-surface-200 bg-white p-5">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Flagged</p>
              <p className={`mt-2 text-3xl font-black tabular-nums ${flagged > 0 ? 'text-red-600' : 'text-ink'}`}>{flagged}</p>
              <p className="text-[11px] text-ink-light mt-1">score &gt; 60</p>
            </div>

            <div className="rounded-xl border border-surface-200 bg-white p-5">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Trend</p>
              <p className={`mt-2 text-3xl font-black ${trendColor}`}>{trendIcon}</p>
              <p className={`text-[11px] mt-1 ${trendColor}`}>{trendLabel}</p>
            </div>
          </div>

          {/* Distribution + Categories row */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Score Distribution */}
            <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-surface-100">
                <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Score Distribution</p>
              </div>
              <div className="px-5 py-4 space-y-3">
                {distribution.map((b) => (
                  <div key={b.label} className="flex items-center gap-3">
                    <span className="text-[11px] text-ink-muted w-14 shrink-0 tabular-nums">{b.range}</span>
                    <div className="flex-1 h-5 bg-surface-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${b.color} transition-all duration-500`}
                        style={{ width: `${(b.count / maxBucket) * 100}%`, minWidth: b.count > 0 ? '8px' : '0' }}
                      />
                    </div>
                    <span className="text-[11px] font-medium text-ink tabular-nums w-6 text-right">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Categories */}
            <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-surface-100">
                <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Top Patterns Detected</p>
              </div>
              {topCategories.length > 0 ? (
                <div className="px-5 py-4 space-y-3">
                  {topCategories.map(([cat, count]) => {
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={cat} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-ink font-medium">{formatCategory(cat)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-ink-muted tabular-nums w-8 text-right">{count}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-emerald-600 font-medium">All clean</p>
                  <p className="text-xs text-ink-light mt-1">No toxic patterns detected yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Trend detail (if enough data) */}
          {trend.direction !== 'stable' && (
            <div className={`mb-6 rounded-xl border p-4 flex items-center gap-3 ${
              trend.direction === 'down' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
            }`}>
              <span className={`text-2xl ${trendColor}`}>{trendIcon}</span>
              <div>
                <p className={`text-sm font-medium ${trendColor}`}>
                  {trend.direction === 'down'
                    ? `Toxicity is decreasing: ${trend.previous} avg \u2192 ${trend.recent} avg`
                    : `Toxicity is increasing: ${trend.previous} avg \u2192 ${trend.recent} avg`
                  }
                </p>
                <p className="text-xs text-ink-muted mt-0.5">
                  Based on comparing your recent vs. earlier analyses
                </p>
              </div>
            </div>
          )}

          {/* Recent Analyses */}
          <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-surface-100 flex items-center justify-between">
              <p className="text-[11px] font-medium text-ink-muted uppercase tracking-wider">Recent Analyses</p>
              <p className="text-[11px] text-ink-light">{total} total</p>
            </div>
            <div className="divide-y divide-surface-100">
              {analyses.slice(0, 25).map((a) => {
                const style = scoreStyle(a.toxicity_score);
                const isClean = a.primary_category === 'none' || a.toxicity_score <= 20;
                const isSender = a.analysis_mode === 'sender';
                return (
                  <div key={a.id} className="flex items-center gap-4 px-5 py-3 hover:bg-surface-50 transition-colors">
                    {/* Score badge */}
                    <div className={`shrink-0 w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center`}>
                      <span className={`text-sm font-black tabular-nums ${style.text}`}>{a.toxicity_score}</span>
                    </div>

                    {/* Category + severity */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-ink">
                          {isClean ? 'Clean' : formatCategory(a.primary_category)}
                        </span>
                        {!isClean && a.primary_severity && (
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${sevColor(a.primary_severity)}`}>
                            {a.primary_severity}
                          </span>
                        )}
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                          isSender
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            : 'bg-red-50 text-red-500 border border-red-200'
                        }`}>
                          {isSender ? 'sender' : 'shield'}
                        </span>
                        {a.action_taken === 'quarantined' && (
                          <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-medium">
                            quarantined
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${style.text}`}>{style.label}</p>
                    </div>

                    {/* Score bar mini */}
                    <div className="hidden sm:block w-20">
                      <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${style.barColor} transition-all`}
                          style={{ width: `${a.toxicity_score}%` }}
                        />
                      </div>
                    </div>

                    {/* Time */}
                    <span className="text-[11px] text-ink-light tabular-nums shrink-0 w-16 text-right">
                      {timeAgo(a.analyzed_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Privacy note */}
          <p className="text-center text-[11px] text-surface-300 mt-6">
            Only metadata is stored. AAS never saves email content.
          </p>
        </>
      )}
    </div>
  );
}
